import dayjs from "dayjs";

export const QUESTION_TYPES = [
    { label: "Short Text", value: 1 },
    { label: "Long Text", value: 2 },
    { label: "Single Choice", value: 3 },
    { label: "Multi Choice", value: 4 },
    { label: "Date", value: 5 },
    { label: "Paragraph", value: 6 },
];

export const DEFAULT_OPTION_SETS = [
    {
        label: "Option Set 1",
        options: ["Strongly Disagree", "Disagree", "Neutral", "Agree", "Strongly Agree"],
    },
    {
        label: "Option Set 2",
        options: ["Loved It", "Sometime", "Not Really"],
    },
];

export const getTypeLabel = (value) =>
    QUESTION_TYPES.find((t) => t.value === value)?.label || value || "-";

export const getQuestionId = (q) => q?.questionId ?? q?.id ?? null;

/** API field names are read tolerantly so a minor rename on the server does not blank the column. */
const firstOf = (obj, keys) => {
    for (const key of keys) {
        const value = obj?.[key];
        if (value !== undefined && value !== null && value !== "") return value;
    }
    return null;
};

export const getAudit = (q) => ({
    updateCount: firstOf(q, ["updateCount", "updatedCount", "editCount", "totalUpdates"]) ?? 0,
    lastUpdatedOn: firstOf(q, ["lastUpdatedOn", "updatedOn", "modifiedOn", "lastModifiedOn"]),
    lastUpdatedByName: firstOf(q, [
        "lastUpdatedByName",
        "updatedByName",
        "modifiedByName",
        "lastUpdatedBy",
    ]),
    createdOn: firstOf(q, ["createdOn", "createdDate", "addedOn"]),
    createdByName: firstOf(q, ["createdByName", "createdBy", "addedByName"]),
});

/** How many students have already answered this question (0 when the server does not send it). */
export const getAttemptCount = (q) =>
    Number(firstOf(q, ["attemptCount", "studentAttemptCount", "answeredCount", "responseCount"])) || 0;

/** True once the backend sends change-tracking fields, so history UI can switch itself on. */
export const hasAuditFields = (rows = []) =>
    rows.some(
        (r) =>
            r?.updateCount !== undefined ||
            r?.updatedCount !== undefined ||
            r?.lastUpdatedOn !== undefined ||
            r?.updatedOn !== undefined
    );

export const formatDateTime = (value) => {
    if (!value) return "-";
    const d = dayjs(value);
    return d.isValid() ? d.format("DD MMM YYYY, h:mm A") : "-";
};

export const formatDate = (value) => {
    if (!value) return "-";
    const d = dayjs(value);
    return d.isValid() ? d.format("DD MMM YYYY") : "-";
};

/** Flattens the GetAssessmentTemplate response into table rows that remember their module. */
export const flattenModules = (modules = []) =>
    modules.flatMap((m) => {
        const moduleId = m?.id ?? m?.moduleId;
        const moduleName = m?.name ?? m?.moduleName ?? "";
        const moduleSortOrder = m?.sortOrder ?? m?.SortOrder ?? 0;
        return (m?.questions || []).map((q) => ({
            ...q,
            moduleId: q?.moduleId ?? moduleId,
            moduleName: q?.moduleName ?? moduleName,
            moduleSortOrder,
        }));
    });

export const sortRows = (rows = []) =>
    [...rows].sort(
        (a, b) =>
            (a.moduleSortOrder || 0) - (b.moduleSortOrder || 0) ||
            String(a.moduleName || "").localeCompare(String(b.moduleName || "")) ||
            (a.sortOrder || 0) - (b.sortOrder || 0)
    );

export const isChoiceType = (type) => type === 3 || type === 4;

/** One-line summary of the answer for the table, so the row says something without expanding. */
export const answerSummary = (q) => {
    if (!q) return "-";
    if (isChoiceType(q.questionType)) {
        if (q.isDefaultOptions) return `Default option set ${q.defaultOptionSet || 1}`;
        const total = (q.options || []).length;
        const correct = (q.options || []).filter((o) => o.isCorrect).length;
        return `${total} options${correct ? `, ${correct} correct` : ""}`;
    }
    if (q.questionType === 6) return `${(q.childQuestions || []).length} sub-questions`;
    if (q.questionType === 5) return q.answerKeyDate ? formatDate(q.answerKeyDate) : "No answer key";
    return q.answerKeyText ? q.answerKeyText : "No answer key";
};

export const optionsAsLines = (q) => {
    if (!q) return [];
    if (q.isDefaultOptions) {
        const set = DEFAULT_OPTION_SETS[(q.defaultOptionSet || 1) - 1];
        return (set?.options || []).map((text, i) => `${i + 1}. ${text}`);
    }
    return (q.options || []).map(
        (o, i) => `${i + 1}. ${o.optionText}${o.isCorrect ? "  (correct)" : ""}`
    );
};

/* ------------------------------------------------------------------ *
 * Version comparison — the server stores snapshots, the UI diffs them
 * ------------------------------------------------------------------ */

export const FIELD_LABELS = {
    questionText: "Question",
    questionType: "Type",
    syllabusId: "Syllabus",
    answerKeyText: "Answer key",
    answerKeyDate: "Answer key date",
    sortOrder: "Order",
    questionMediaUrl: "Image",
    isDefaultOptions: "Uses default options",
    defaultOptionSet: "Default option set",
    options: "Options",
    childQuestions: "Sub-questions",
};

export const parseSnapshot = (snapshot) => {
    if (!snapshot) return null;
    if (typeof snapshot === "string") {
        try {
            return JSON.parse(snapshot);
        } catch {
            return null;
        }
    }
    return snapshot;
};

const readableValue = (field, value, { syllabusNameById } = {}) => {
    if (value === undefined || value === null || value === "") return "—";
    switch (field) {
        case "questionType":
            return getTypeLabel(value);
        case "syllabusId":
            return syllabusNameById?.[value] || `#${value}`;
        case "answerKeyDate":
            return formatDate(value);
        case "isDefaultOptions":
            return value ? "Yes" : "No";
        case "defaultOptionSet":
            return value ? `Set ${value}` : "—";
        case "options":
            return (value || [])
                .map((o, i) => `${i + 1}. ${o.optionText}${o.isCorrect ? "  (correct)" : ""}`)
                .join("\n");
        case "childQuestions":
            return (value || []).map((c, i) => `${i + 1}. ${c.questionText}`).join("\n");
        default:
            return String(value);
    }
};

/**
 * Compares two question snapshots and returns only what changed,
 * already turned into words a teacher can read.
 */
export const diffVersions = (before, after, opts = {}) => {
    const older = parseSnapshot(before) || {};
    const newer = parseSnapshot(after) || {};

    return Object.keys(FIELD_LABELS).reduce((changes, field) => {
        const a = readableValue(field, older[field], opts);
        const b = readableValue(field, newer[field], opts);
        if (a !== b) changes.push({ field, label: FIELD_LABELS[field], before: a, after: b });
        return changes;
    }, []);
};

/** Names of the fields a history row reports as changed, in plain words. */
export const changedFieldLabels = (version, previousVersion, opts = {}) => {
    const reported = version?.changedFields;
    if (Array.isArray(reported) && reported.length) {
        return reported.map((f) => FIELD_LABELS[f] || f);
    }
    if (typeof reported === "string" && reported.trim()) {
        return reported
            .split(",")
            .map((f) => FIELD_LABELS[f.trim()] || f.trim())
            .filter(Boolean);
    }
    if (!previousVersion) return [];
    return diffVersions(previousVersion.snapshot, version?.snapshot, opts).map((c) => c.label);
};

/* ------------------------------------------------------------------ *
 * Payload building — shared by add, edit, duplicate and move
 * ------------------------------------------------------------------ */

const buildOptions = (q, { keepIds }) => {
    if (!isChoiceType(q.questionType) || q.isDefaultOptions) return [];
    return (q.options || []).map((opt, idx) => ({
        optionText: opt.optionText,
        optionMediaUrl: opt.optionMediaUrl || "",
        isCorrect: !!opt.isCorrect,
        sortOrder: idx + 1,
        ...(keepIds && opt.optionId ? { optionId: opt.optionId } : {}),
    }));
};

/**
 * Builds the Add/Update question payload from a question object.
 * `keepIds` keeps questionId/optionId (edit, move); dropping them makes it a new question (duplicate).
 */
export const buildQuestionPayload = (question, { moduleId, keepIds = true, overrides = {} } = {}) => {
    const q = { ...question, ...overrides };
    const targetModuleId = moduleId ?? q.moduleId;

    return {
        moduleId: targetModuleId,
        syllabusId: q.syllabusId ?? q.SyllabusId ?? null,
        questionText: q.questionText || "",
        questionMediaUrl: q.questionMediaUrl || "",
        questionType: q.questionType,
        answerKeyText: q.answerKeyText || "",
        answerKeyDate:
            q.questionType === 5 && q.answerKeyDate ? dayjs(q.answerKeyDate).toISOString() : null,
        marks: 0,
        sortOrder: Number(q.sortOrder) || 1,
        isDefaultOptions: !!q.isDefaultOptions,
        defaultOptionSet: q.isDefaultOptions ? q.defaultOptionSet || 0 : 0,
        options: buildOptions(q, { keepIds }),
        childQuestions:
            q.questionType === 6
                ? (q.childQuestions || []).map((child, idx) => ({
                    moduleId: targetModuleId,
                    questionText: child.questionText || "",
                    questionMediaUrl: child.questionMediaUrl || "",
                    questionType: child.questionType,
                    answerKeyText: child.answerKeyText || "",
                    answerKeyDate:
                        child.questionType === 5 && child.answerKeyDate
                            ? dayjs(child.answerKeyDate).toISOString()
                            : null,
                    marks: child.marks || 0,
                    sortOrder: Number(child.sortOrder) || idx + 1,
                    isDefaultOptions: !!child.isDefaultOptions,
                    defaultOptionSet: child.isDefaultOptions ? child.defaultOptionSet || 0 : 0,
                    options: buildOptions(child, { keepIds }),
                    syllabusId: child.syllabusId ?? child.SyllabusId ?? null,
                    ...(keepIds && child.questionId ? { questionId: child.questionId } : {}),
                }))
                : [],
    };
};

/** Rows for the Excel export, one line per question. */
export const toExportRows = (rows = [], { syllabusNameById = {} } = {}) =>
    rows.map((q, index) => {
        const audit = getAudit(q);
        return {
            "Sl No": index + 1,
            Module: q.moduleName || "",
            Syllabus: syllabusNameById[q.syllabusId] || "",
            Order: q.sortOrder ?? "",
            Question: q.questionText || "",
            Type: getTypeLabel(q.questionType),
            "Answer / Options": optionsAsLines(q).join(" | ") || q.answerKeyText || "",
            "Sub-questions": (q.childQuestions || []).length,
            "Times updated": audit.updateCount ?? 0,
            "Last updated": audit.lastUpdatedOn ? formatDateTime(audit.lastUpdatedOn) : "",
            "Last updated by": audit.lastUpdatedByName || "",
            "Created on": audit.createdOn ? formatDateTime(audit.createdOn) : "",
            "Created by": audit.createdByName || "",
        };
    });
